import { Center, Flex, Loader, Stack } from '@mantine/core';
import { GetSensorResponse } from 'api/actions/get-sensor/get-sensor-response';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import panic from 'panic';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import SensorInfo from 'components/SensorInfo';
import SensorAlerts from 'components/SensorAlerts';
import SensorStatistics from 'components/SensorStatistics';
import EnergySpotComparison from 'components/EnergySpotComparison/EnergySpotComparison';
import {
  backgroundBar,
  lineChartGradientBackground,
  createBarChartOptions,
  createLineChartOptions,
} from 'pages/sensors/chartUtils';
import { withBrowserTimezone } from 'env/browser-timezone';

// Register required Chart.js components.
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Sensor = GetSensorResponse['sensor'];

// ── TODO: remove static mock ──────────────────────────────────────────────────
// /** Generates static mock energyComparison data for testing Measuring Type ID 17 */
// function generateMockEnergyComparison(): Sensor['energyComparison'] {
//   const now = new Date();
//   const todayStr = now.toISOString().substring(0, 10);
//
//   /** Generate daily savings for a range */
//   const generateDailySavings = (days: number, startOffset: number) => {
//     const result: { date: string; consumption: number; spotCost: number; fixedCost: number; savings: number }[] = [];
//     for (let d = 0; d < days; d++) {
//       const date = new Date();
//       date.setUTCDate(date.getUTCDate() - startOffset - d);
//       const dateStr = date.toISOString().substring(0, 10);
//       const consumption = Math.round(8000 + Math.random() * 12000);
//       const spotPrice = 80 + Math.random() * 120;
//       const fixedPrice = 120;
//       const spotCost = (consumption * spotPrice) / 1_000_000;
//       const fixedCost = (consumption * fixedPrice) / 1_000_000;
//       result.unshift({ date: dateStr, consumption, spotCost, fixedCost, savings: fixedCost - spotCost });
//     }
//     return result;
//   };
//
//   /** Generate 15-minute intervals for today */
//   const generateIntervals = () => {
//     const intervals: { label: string; consumption?: number; spotCost: number; fixCost: number }[] = [];
//     const nowHour = now.getUTCHours();
//     const nowMinute = now.getUTCMinutes();
//     for (let i = 0; i < 96; i++) {
//       const hour = Math.floor(i / 4);
//       const minute = (i % 4) * 15;
//       // Only past intervals have data
//       const hasData = hour < nowHour || (hour === nowHour && minute <= nowMinute);
//       const label = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
//       const consumption = hasData ? Math.round(100 + Math.random() * 400) : undefined;
//       const spotPrice = 80 + Math.random() * 120;
//       intervals.push({
//         label,
//         consumption,
//         spotCost: consumption ? (consumption * spotPrice) / 1_000_000 : 0,
//         fixCost: consumption ? (consumption * 120) / 1_000_000 : 0,
//       });
//     }
//     return intervals;
//   };
//
//   return {
//     dnes: { intervals: generateIntervals() },
//     tyzden: { dailySavings: generateDailySavings(7, 0) },
//     mesiac: { dailySavings: generateDailySavings(now.getUTCDate(), 0) },
//     'minuly-mesiac': { dailySavings: generateDailySavings(30, now.getUTCDate()) },
//     polrok: { dailySavings: generateDailySavings(180, 0) },
//     rok: { dailySavings: generateDailySavings(365, 0) },
//   };
// }
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Detail of a sensor.
 */
export function SensorDetailImpl({ sensorId }: { sensorId: number }) {
  const { lang, _t } = useLang();
  const { getAction } = useApi();
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);

  const graphType = (sensor?.chart as any)?.graphType;
  // console.log(graphType);

  useEffect(() => {
    if (sensorId) {
      const getSensor = getAction('GetSensor');

      getSensor({ parameters: { sensorId }, query: withBrowserTimezone({}) })
        .then(({ sensor }) => {
          // ── TODO: remove static mock ──────────────────────────────────────────
          // Static energyComparison mock for testing Measuring Type ID 17
          // sensor.energyComparison = generateMockEnergyComparison();
          // ───────────────────────────────────────────────────────────────────────
          setSensor(sensor);
          setSelectedTitle(sensor.chart?.views?.[0]?.title ?? null);
        })
        .catch(panic);
    }
  }, [sensorId]);

  /* Title TAG názov senzora */
  useEffect(() => {
    if (sensor?.name) {
      document.title = sensor.name;
    }
  }, [sensor?.name]);

  // Function to calculate average value based on selected title
  const calculateAverageValue = useMemo(() => {
    if (!sensor || !sensor.chart || !sensor.chart.views) return 0;

    const chartView = sensor.chart.views.find((view) => view.title === selectedTitle);
    if (!chartView || !chartView.data || chartView.data.length === 0) return 0;

    const total = chartView.data.reduce((acc, item) => acc + (item.value ?? 0), 0);
    return (total / chartView.data.length).toFixed(2); // Return average value rounded to two decimal places
  }, [sensor, selectedTitle]);

  // Function to calculate aggregate (sum) value based on selected title
  const calculateAggregateValue = useMemo(() => {
    if (!sensor || !sensor.chart || !sensor.chart.views) return 0;

    const chartView = sensor.chart.views.find((view) => view.title === selectedTitle);
    if (!chartView || !chartView.data || chartView.data.length === 0) return 0;

    const total = chartView.data.reduce((acc, item) => acc + (item.value ?? 0), 0);
    return total.toFixed(2); // Return total value rounded to two decimal places
  }, [sensor, selectedTitle]);

  /**
   * Get the range for a selected title.
   *
   * @param title The title of the chart view
   * @returns The range of the chart view if found, otherwise an empty string
   */
  const getRangeForSelectedTitle = (title: string) => {
    if (!sensor || !sensor.chart || !sensor.chart.views) return '';

    const chartView = sensor.chart.views.find((view) => view.title === title);
    return chartView ? chartView.range : '';
  };

  const chartData = useMemo(() => {
    if (!sensor || !sensor.chart || !sensor.chart.views) {
      return null;
    }

    const chartViews = sensor.chart.views.find((view) => view.title === selectedTitle);
    if (!chartViews || !chartViews.data) {
      return null;
    }

    const dataValues = chartViews.data.map((item) => item.value ?? 0);

    // Create different dataset configurations based on graph type
    if (graphType === 'line') {
      return {
        labels: chartViews.data.map((item) => item.labelShort),
        datasets: [
          {
            // Include required properties for SensorStatistics component
            barPercentage: 0.6,
            label: 'Line Data',
            data: dataValues,
            backgroundColor: 'rgba(85, 31, 255, 0.4)',
            hoverBackgroundColor: '#1F212A',
            borderRadius: 5,
            borderSkipped: false,

            // Line chart specific properties (these will be used by the chart but ignored by type checking)
            type: 'line' as any,
            borderColor: '#551FFF',
            borderWidth: 4,
            tension: 0.1,
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: '#000000',
            pointBorderWidth: 3,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      };
    } else {
      return {
        labels: chartViews.data.map((item) => item.labelShort),
        datasets: [
          {
            barPercentage: 0.6,
            label: 'Blue Bars',
            data: dataValues,
            backgroundColor: '#551FFF',
            hoverBackgroundColor: '#1F212A',
            borderRadius: 5,
            borderSkipped: false,
          },
        ],
      };
    }
  }, [sensor, selectedTitle, graphType]);

  // Get chart options based on the graph type
  const chartOptions = useMemo(() => {
    if (!sensor) {
      // Return default chart options if sensor is null
      // Using the bar chart options type as default since it's simpler
      return {
        scales: {
          x: {
            ticks: { color: theme.colors?.gray?.[7] },
            grid: { drawBorder: false, display: false },
            border: { display: false },
          },
          y: {
            ticks: { color: theme.colors?.gray?.[7] },
            grid: { drawBorder: false, display: false },
          },
        },
        plugins: {
          tooltip: {
            enabled: false,
            callbacks: {
              title: () => '',
              label: () => '',
            },
          },
          legend: { display: false },
        },
      };
    }

    return graphType === 'line'
      ? createLineChartOptions(sensor, selectedTitle)
      : createBarChartOptions(sensor, selectedTitle);
  }, [graphType, sensor, selectedTitle]);

  if (!sensor) {
    return (
      <Center style={{ height: '100vh' }}>
        <Flex direction="column" align="center" gap="1rem">
          <Loader size="lg" color="#551FFF" />
          <H2SemiBold>{_t('Loading sensor')}</H2SemiBold>
        </Flex>
      </Center>
    );
  }

  const titles = sensor.chart?.views?.map((view) => view.title) || [];

  return (
    <Stack bg="gray.0" py={20} px={16} mih="100vh">
      <Flex justify="space-between" align="center" mb={6} gap={16}>
        {/**
        {isFavorite ? (
          <span onClick={handleFavoriteClick}>
            <FavoriteEnabledIcon />
          </span>
        ) : (
          <span onClick={handleFavoriteClick}>
            <FavoriteDisabledIcon />
          </span>
        )}
          **/}
      </Flex>
      <SensorInfo sensor={sensor} lang={lang} />

      <SensorAlerts sensor={sensor} _t={_t} />

      {/* Hide the primary chart for electricity meters (Measuring Type ID 17) — EnergySpotComparison shows the same data */}
      {!sensor.energyComparison && (
        <SensorStatistics
          calculateAverageValue={calculateAverageValue}
          calculateAggregateValue={calculateAggregateValue}
          sensor={sensor}
          getRangeForSelectedTitle={getRangeForSelectedTitle}
          selectedTitle={selectedTitle}
          setSelectedTitle={setSelectedTitle}
          titles={titles}
          chartData={chartData}
          chartOptions={chartOptions}
          backgroundBar={graphType === 'line' ? lineChartGradientBackground : backgroundBar}
          _t={_t}
        />
      )}

      {/* Energy SPOT vs FIX comparison — only for Measuring Type ID 17 */}
      {sensor?.energyComparison && (
        <EnergySpotComparison energyComparison={sensor.energyComparison} sensorId={sensorId} />
      )}
    </Stack>
  );
}

/**
 * Detail of a sensor.
 */
export default function SensorDetail() {
  const { sensorId: sensorIdRaw = '' } = useParams();
  const sensorId = useMemo(() => {
    const parsedSensorId = parseInt(sensorIdRaw);
    return parsedSensorId;
  }, [sensorIdRaw]);

  return <SensorDetailImpl sensorId={sensorId} />;
}
